import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/api';
import type { ApartmentDefaults } from '../types/apartments';
import { Navbar } from './Navbar';
import { PdfModal } from './PdfModal';
import { datadogRum } from '@datadog/browser-rum';

export const ApartmentDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [apartment, setApartment] = useState<ApartmentDefaults | null>(null);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [activeModal, setActiveModal] = useState<'receipt' | 'payment-notice' | 'charge-regularization' | 'rent-increase' | null>(null);

  useEffect(() => {
    datadogRum.startView({ name: 'Apartment Detail', service: 'frontend', version: '1.0' });
    
    let isMounted = true;
    (async () => {
      if (!slug) {
        navigate('/apartments');
        return;
      }
      
      try {
        const res = await apiService.getApartments();
        if (isMounted) {
          if (res[slug]) {
            setApartment(res[slug]);
          } else {
            setError('Apartment not found');
          }
        }
      } catch (e) {
        if (isMounted) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [slug, navigate]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">{t('common.loading')}</div>;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex flex-col items-center justify-center py-12">
          <div className="text-red-600 mb-4">{error}</div>
          <button
            onClick={() => navigate('/apartments')}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            {t('apartmentDetail.backToApartments')}
          </button>
        </div>
      </div>
    );
  }

  if (!apartment) {
    return null;
  }

  const tenants = (apartment.tenants || []).map(t => t.name).join(' & ');
  const address = apartment.apartment?.address || '-';
  
  // Get rent details - use rentDetails if available, otherwise fall back to rent/charges
  const rentDetails = apartment.apartment?.rentDetails && apartment.apartment.rentDetails.length > 0
    ? apartment.apartment.rentDetails
    : [
        { title: 'Loyer', value: apartment.apartment?.rent || 0 },
        { title: 'Provision pour charges', value: apartment.apartment?.charges || 0 }
      ];
  
  const total = rentDetails.reduce((sum, detail) => {
    const value = typeof detail.value === 'string' ? parseFloat(detail.value) || 0 : detail.value;
    return sum + value;
  }, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="py-4 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate('/apartments')}
            className="mb-3 inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('apartmentDetail.backToApartments')}
          </button>

          {/* Apartment Summary */}
          <div className="bg-white shadow-lg rounded-lg overflow-hidden mb-4">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-6">
              <div className="flex items-center">
                <svg className="w-12 h-12 text-white opacity-90 mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                </svg>
                <div>
                  <h1 className="text-2xl font-bold text-white">{apartment.name || slug}</h1>
                  <p className="text-indigo-100 text-sm mt-1">{address}</p>
                </div>
              </div>
            </div>

            <div className="px-6 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-1">{t('apartments.tenants')}</p>
                  <p className="text-base font-semibold text-gray-900">{tenants || t('apartments.noTenants')}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-2">{t('pdfModal.rentDetails')}</p>
                  <div className="space-y-1">
                    {rentDetails.map((detail, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <span className="text-gray-600">{detail.title}:</span>
                        <span className="font-semibold text-gray-900">{detail.value}€</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-4 bg-indigo-50 rounded-lg p-3 border-2 border-indigo-200">
                <div className="flex justify-between items-center">
                  <p className="text-xs text-indigo-700 font-medium uppercase tracking-wider">{t('apartmentDetail.totalMonthlyPayment')}</p>
                  <p className="text-2xl font-bold text-indigo-900">{total.toFixed(2)}€</p>
                </div>
              </div>
            </div>
          </div>

          {/* PDF Generation Section */}
          <div className="mb-3">
            <h2 className="text-xl font-bold text-gray-900 mb-2">{t('apartmentDetail.generateDocuments')}</h2>
            <p className="text-sm text-gray-600 mb-3">{t('apartmentDetail.generateDocumentsSubtitle')}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Rent Receipt Card */}
            <button
              onClick={() => setActiveModal('receipt')}
              className="bg-white shadow-lg rounded-lg p-5 text-left hover:shadow-xl transition-all duration-300 hover:scale-105 border-2 border-transparent hover:border-indigo-500"
            >
              <div className="flex items-center mb-3">
                <div className="bg-indigo-100 p-2 rounded-lg">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-gray-900 ml-3">{t('apartmentDetail.quittanceTitle')}</h3>
              </div>
              <p className="text-sm text-gray-600 mb-3">{t('apartmentDetail.quittanceDescription')}</p>
              <div className="flex items-center text-indigo-600 font-medium text-sm">
                <span>{t('apartmentDetail.openForm')}</span>
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>

            {/* Payment Notice Card */}
            <button
              onClick={() => setActiveModal('payment-notice')}
              className="bg-white shadow-lg rounded-lg p-5 text-left hover:shadow-xl transition-all duration-300 hover:scale-105 border-2 border-transparent hover:border-purple-500"
            >
              <div className="flex items-center mb-3">
                <div className="bg-purple-100 p-2 rounded-lg">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-gray-900 ml-3">{t('apartmentDetail.echeanceTitle')}</h3>
              </div>
              <p className="text-sm text-gray-600 mb-3">{t('apartmentDetail.echeanceDescription')}</p>
              <div className="flex items-center text-purple-600 font-medium text-sm">
                <span>{t('apartmentDetail.openForm')}</span>
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>

            {/* Charge Regularization Card */}
            <button
              onClick={() => setActiveModal('charge-regularization')}
              className="bg-white shadow-lg rounded-lg p-5 text-left hover:shadow-xl transition-all duration-300 hover:scale-105 border-2 border-transparent hover:border-teal-500"
            >
              <div className="flex items-center mb-3">
                <div className="bg-teal-100 p-2 rounded-lg">
                  <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-gray-900 ml-3">{t('apartmentDetail.regularisationTitle')}</h3>
              </div>
              <p className="text-sm text-gray-600 mb-3">{t('apartmentDetail.regularisationDescription')}</p>
              <div className="flex items-center text-teal-600 font-medium text-sm">
                <span>{t('apartmentDetail.openForm')}</span>
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>

            {/* Rent Increase Card */}
            <button
              onClick={() => setActiveModal('rent-increase')}
              className="bg-white shadow-lg rounded-lg p-5 text-left hover:shadow-xl transition-all duration-300 hover:scale-105 border-2 border-transparent hover:border-orange-500"
            >
              <div className="flex items-center mb-3">
                <div className="bg-orange-100 p-2 rounded-lg">
                  <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-gray-900 ml-3">{t('apartmentDetail.revisionTitle')}</h3>
              </div>
              <p className="text-sm text-gray-600 mb-3">{t('apartmentDetail.revisionDescription')}</p>
              <div className="flex items-center text-orange-600 font-medium text-sm">
                <span>{t('apartmentDetail.openForm')}</span>
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      {slug && apartment && activeModal && (
        <PdfModal
          isOpen={activeModal !== null}
          onClose={() => setActiveModal(null)}
          type={activeModal}
          slug={slug}
          apartment={apartment}
        />
      )}
    </div>
  );
};

